import { BillingDetailsInput } from '@sc/graphql';
import { isEuCountryCode } from '../utility/countries';
import { withFieldGroup } from '../forms/appForm';

export interface BillingDetailsValue {
  country: string;
  vatNumber: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
}

export const EMPTY_BILLING_DETAILS: BillingDetailsValue = {
  country: '',
  vatNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
};

/**
 * Normalise the validated fields into the mutation input. The VAT number is
 * EU-only; its validity is the backend's call at savePaymentMethod time.
 */
export const toBillingDetailsInput = (
  value: BillingDetailsValue
): BillingDetailsInput => ({
  enteredCountry: value.country,
  vatNumber:
    isEuCountryCode(value.country) && value.vatNumber.trim()
      ? value.vatNumber.trim()
      : null,
  addressLine1: value.addressLine1.trim(),
  addressLine2: value.addressLine2.trim() || null,
  city: value.city.trim(),
  postcode: value.postcode.trim(),
});

const required =
  (message: string) =>
  ({ value }: { value: string }) =>
    value.trim() ? undefined : message;

// A field group mounted into the checkout form (`fields` names the parent key),
// so validation and values live on the one form instance.
export const BillingDetailsFields = withFieldGroup({
  defaultValues: EMPTY_BILLING_DETAILS,
  render: ({ group }) => (
    <div className="flex flex-col gap-4">
      <group.AppField
        name="country"
        validators={{ onSubmit: required('Select your country.') }}
        children={(field) => <field.CountryField label="Country" />}
      />

      <group.Subscribe
        selector={(state) => state.values.country}
        children={(country) =>
          isEuCountryCode(country) && (
            <group.AppField
              name="vatNumber"
              children={(field) => <field.TextField label="VAT number" />}
            />
          )
        }
      />

      <group.AppField
        name="addressLine1"
        validators={{ onSubmit: required('Address is required.') }}
        children={(field) => <field.TextField label="Address line 1" />}
      />

      <group.AppField
        name="addressLine2"
        children={(field) => (
          <field.TextField label="Address line 2 (optional)" />
        )}
      />

      <div className="grid grid-cols-2 gap-3">
        <group.AppField
          name="city"
          validators={{ onSubmit: required('City is required.') }}
          children={(field) => <field.TextField label="City" />}
        />
        <group.AppField
          name="postcode"
          validators={{ onSubmit: required('Postcode is required.') }}
          children={(field) => <field.TextField label="Postcode" />}
        />
      </div>
    </div>
  ),
});
